import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Geolocation } from '@capacitor/geolocation';
import { Preferences } from '@capacitor/preferences';

export interface PermissionStatus {
  location: 'granted' | 'denied' | 'prompt';
  microphone: 'granted' | 'denied' | 'prompt';
}

const LOCATION_PERMISSION_KEY = 'location_permission_requested';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {

  constructor(private platform: Platform) { }

  /**
   * Request both location and microphone permissions for Tours
   */
  async requestToursPermissions(): Promise<PermissionStatus> {
    const locationStatus = await this.requestLocationPermission();
    const microphoneStatus = await this.requestMicrophonePermission();

    return {
      location: locationStatus,
      microphone: microphoneStatus
    };
  }

  /**
   * Request location permission using Capacitor Geolocation plugin on native
   */
  async requestLocationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      if (this.platform.is('capacitor')) {
        // Native platform - use Capacitor Geolocation plugin
        const permissions = await Geolocation.requestPermissions();
        return this.mapCapacitorPermissionState(permissions.location);
      } else {
        // Web - use Permissions API
        if (!navigator.permissions) {
          return 'prompt';
        }

        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        return result.state as 'granted' | 'denied' | 'prompt';
      }
    } catch (error) {
      console.error('Error requesting location permission:', error);
      return 'denied';
    }
  }

  /**
   * Request location permission at app startup for smart home automations.
   * Only requests if not previously requested or denied.
   * Returns true if permission was granted.
   */
  async requestLocationForSmartHome(): Promise<boolean> {
    try {
      // Check if we've already requested and user denied
      const hasRequested = await this.hasRequestedLocationPermission();
      const currentStatus = await this.checkLocationPermission();

      // If already granted, we're done
      if (currentStatus === 'granted') {
        return true;
      }

      // If user previously denied, don't ask again (they'd need to go to settings)
      if (currentStatus === 'denied' && hasRequested) {
        console.log('Location permission previously denied, not requesting again');
        return false;
      }

      // Request permission
      const result = await this.requestLocationPermission();

      // Mark that we've requested
      await this.markLocationPermissionRequested();

      return result === 'granted';
    } catch (error) {
      console.error('Error requesting location for smart home:', error);
      return false;
    }
  }

  /**
   * Check if we've previously requested location permission
   */
  private async hasRequestedLocationPermission(): Promise<boolean> {
    try {
      const { value } = await Preferences.get({ key: LOCATION_PERMISSION_KEY });
      return value === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Mark that we've requested location permission
   */
  private async markLocationPermissionRequested(): Promise<void> {
    try {
      await Preferences.set({ key: LOCATION_PERMISSION_KEY, value: 'true' });
    } catch (error) {
      console.error('Error saving location permission state:', error);
    }
  }

  /**
   * Map Capacitor permission state to our standard states
   */
  private mapCapacitorPermissionState(state: string): 'granted' | 'denied' | 'prompt' {
    switch (state) {
      case 'granted':
        return 'granted';
      case 'denied':
        return 'denied';
      case 'prompt':
      case 'prompt-with-rationale':
      default:
        return 'prompt';
    }
  }

  /**
   * Request microphone permission
   */
  async requestMicrophonePermission(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      // Request microphone access through getUserMedia
      // This works on iOS, Android (via WebView), and Web
      await navigator.mediaDevices.getUserMedia({ audio: true });
      return 'granted';
    } catch (error: any) {
      console.error('Error requesting microphone permission:', error);

      // Check the error type
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        return 'denied';
      }

      return 'denied';
    }
  }

  /**
   * Check microphone permission status without requesting
   */
  async checkMicrophonePermission(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      if (!navigator.permissions) {
        return 'prompt';
      }

      // @ts-ignore - microphone permission is valid but TypeScript may not recognize it
      const result = await navigator.permissions.query({ name: 'microphone' });
      return result.state as 'granted' | 'denied' | 'prompt';
    } catch (error) {
      console.error('Error checking microphone permission:', error);
      return 'prompt';
    }
  }

  /**
   * Check location permission status without requesting
   */
  async checkLocationPermission(): Promise<'granted' | 'denied' | 'prompt'> {
    try {
      if (this.platform.is('capacitor')) {
        // Native platform - use Capacitor Geolocation plugin
        const permissions = await Geolocation.checkPermissions();
        return this.mapCapacitorPermissionState(permissions.location);
      } else {
        // Web - use Permissions API
        if (!navigator.permissions) {
          return 'prompt';
        }

        const result = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        return result.state as 'granted' | 'denied' | 'prompt';
      }
    } catch (error) {
      console.error('Error checking location permission:', error);
      return 'prompt';
    }
  }

  /**
   * Get current status of both permissions
   */
  async getToursPermissionStatus(): Promise<PermissionStatus> {
    const locationStatus = await this.checkLocationPermission();
    const microphoneStatus = await this.checkMicrophonePermission();

    return {
      location: locationStatus,
      microphone: microphoneStatus
    };
  }
}
